import React, { useState } from "react";
import "@lemoncode/react-image-focal-point/style.css";
import { ImageFocalPoint } from "@lemoncode/react-image-focal-point";
import styled, { useTheme } from "styled-components";
import { Button } from "@caelor/ui";
import debounce from "lodash.debounce";
import { useTranslation } from "react-i18next";
import { ImageIcon, FocusIcon, InfoIcon } from "./Icons";
import {
  ButtonsContainer,
  CompleteReposition,
  Divider,
  FlexAlign,
  Grid,
  Image,
  ImagePreview,
  Text,
  Wrapper,
} from "./styles";

const ImageFocalPointWrapper = styled.div`
  .focal-point-root {
    max-width: 100%;
  }
  .focal-point-image {
    border-radius: 15px;
  }
  .focal-point-button {
    width: 24px;
    height: 24px;
    border-radius: 4px;
    border: none;
    background-color: ${({ theme }) => theme.border.black15};

    &::after {
      position: absolute;
      content: url("data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMjAiIGhlaWdodD0iMjAiIHZpZXdCb3g9IjAgMCAyMCAyMCIgZmlsbD0ibm9uZSIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj4KPGcgaWQ9IkZyYW1lIiBjbGlwLXBhdGg9InVybCgjY2xpcDBfOTI4Nl8xMzEzNSkiPgo8cGF0aCBpZD0iVmVjdG9yIiBkPSJNMTAuMDAwMyAxOC4zMzM0QzE0LjYwMjcgMTguMzMzNCAxOC4zMzM3IDE0LjYwMjUgMTguMzMzNyAxMC4wMDAxQzE4LjMzMzcgNS4zOTc3MSAxNC42MDI3IDEuNjY2NzUgMTAuMDAwMyAxLjY2Njc1QzUuMzk3OTUgMS42NjY3NSAxLjY2Njk5IDUuMzk3NzEgMS42NjY5OSAxMC4wMDAxQzEuNjY2OTkgMTQuNjAyNSA1LjM5Nzk1IDE4LjMzMzQgMTAuMDAwMyAxOC4zMzM0WiIgc3Ryb2tlPSJ3aGl0ZSIgc3Ryb2tlLXdpZHRoPSIyIiBzdHJva2UtbGluZWNhcD0icm91bmQiIHN0cm9rZS1saW5lam9pbj0icm91bmQiLz4KPHBhdGggaWQ9IlZlY3Rvcl8yIiBkPSJNMTguMzMzMyAxMEgxNSIgc3Ryb2tlPSJ3aGl0ZSIgc3Ryb2tlLXdpZHRoPSIyIiBzdHJva2UtbGluZWNhcD0icm91bmQiIHN0cm9rZS1saW5lam9pbj0icm91bmQiLz4KPHBhdGggaWQ9IlZlY3Rvcl8zIiBkPSJNNS4wMDAzMyAxMEgxLjY2Njk5IiBzdHJva2U9IndoaXRlIiBzdHJva2Utd2lkdGg9IjIiIHN0cm9rZS1saW5lY2FwPSJyb3VuZCIgc3Ryb2tlLWxpbmVqb2luPSJyb3VuZCIvPgo8cGF0aCBpZD0iVmVjdG9yXzQiIGQ9Ik0xMCA1LjAwMDA4VjEuNjY2NzUiIHN0cm9rZT0id2hpdGUiIHN0cm9rZS13aWR0aD0iMiIgc3Ryb2tlLWxpbmVjYXA9InJvdW5kIiBzdHJva2UtbGluZWpvaW49InJvdW5kIi8+CjxwYXRoIGlkPSJWZWN0b3JfNSIgZD0iTTEwIDE4LjMzMzNWMTUiIHN0cm9rZT0id2hpdGUiIHN0cm9rZS13aWR0aD0iMiIgc3Ryb2tlLWxpbmVjYXA9InJvdW5kIiBzdHJva2UtbGluZWpvaW49InJvdW5kIi8+CjwvZz4KPGRlZnM+CjxjbGlwUGF0aCBpZD0iY2xpcDBfOTI4Nl8xMzEzNSI+CjxyZWN0IHdpZHRoPSIyMCIgaGVpZ2h0PSIyMCIgZmlsbD0id2hpdGUiLz4KPC9jbGlwUGF0aD4KPC9kZWZzPgo8L3N2Zz4K");
      top: 2px;
      right: 0px;
      left: 0px;
      bottom: 0px;
    }
  }
`;

function ImageReposition({ openPicker, image, changeImagePosition }) {
  const { t } = useTranslation();
  const theme = useTheme();

  const [isRepositioning, setIsRepositioning] = useState(false);

  const updateImage = (focalPoint) => {
    changeImagePosition(focalPoint);
  };

  const updateImageDebounced = debounce(updateImage, 650);

  return (
    <Wrapper
      onKeyDown={(e) => {
        if (e.key === "Enter") setIsRepositioning(false);
      }}
    >
      {isRepositioning && (
        <>
          <CompleteReposition>
            <Button appearance="link" onClick={() => setIsRepositioning(false)}>
              {t("done")}
            </Button>
          </CompleteReposition>

          <ImageFocalPointWrapper>
            <ImageFocalPoint
              src={image?.link}
              classes={{
                root: "focal-point-root",
                image: "focal-point-image",
                focalPoint: "focal-point-button",
              }}
              onChange={updateImageDebounced}
              focalPoint={image?.focalPoint}
            />
          </ImageFocalPointWrapper>

          <Grid>
            <InfoIcon color={theme.text.text} />
            <Text>{t("image-focus-description")}</Text>
          </Grid>
        </>
      )}
      {!isRepositioning && (
        <ImagePreview>
          <Image src={image?.link} />
          <ButtonsContainer className="buttons">
            <Button appearance="transparent" onClick={() => openPicker()}>
              <FlexAlign>
                <ImageIcon />
                {t("change-image")}
              </FlexAlign>
            </Button>
            <Divider />
            <Button
              appearance="transparent"
              onClick={() => {
                setIsRepositioning(true);
              }}
            >
              <FlexAlign>
                <FocusIcon />
                {t("set-image-focus")}
              </FlexAlign>
            </Button>
          </ButtonsContainer>
        </ImagePreview>
      )}
    </Wrapper>
  );
}

export default ImageReposition;
